import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, Users, Gauge, Fuel, ShieldCheck, MapPin, Crown, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";

const COUNTRY_LABELS = {
  martinique: "Martinique", guadeloupe: "Guadeloupe",
  "saint-martin": "Saint-Martin", "sint-maarten": "Sint Maarten",
};

export function VehicleCard({ v, index = 0 }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  return (
    <div data-testid={`vehicle-card-${v.id}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-search transition-all duration-300 hover:-translate-y-1 border border-border animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}>
      <div className="relative h-52 overflow-hidden">
        <img src={v.photos?.[0]} alt={`${v.brand} ${v.model}`} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-canard-dark">
          {v.category}
        </div>
        {v.superloueur && (
          <div data-testid={`superloueur-badge-${v.id}`} className="absolute bottom-3 left-3 bg-gradient-to-r from-emeraude to-canard text-white px-2.5 py-1 rounded-full text-xs font-extrabold flex items-center gap-1 shadow-cta">
            <Crown className="w-3.5 h-3.5" /> {t("dash.superloueur")}
          </div>
        )}
        {v.boosted && (
          <div data-testid={`boosted-badge-${v.id}`} className="absolute bottom-3 right-3 bg-white/95 backdrop-blur text-canard-dark px-2.5 py-1 rounded-full text-xs font-extrabold flex items-center gap-1 shadow-cta">
            <Rocket className="w-3.5 h-3.5 text-canard" /> {t("search.boosted")}
          </div>
        )}
        {v.rating > 0 && (
          <div className="absolute top-3 right-3 bg-canard-dark/90 text-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Star className="w-3 h-3 fill-emeraude text-emeraude" /> {v.rating}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading font-bold text-lg text-ink">{v.brand} {v.model}</h3>
            <p className="text-xs text-muted2 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {COUNTRY_LABELS[v.country]} · {v.city}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4 text-xs text-muted2 font-semibold">
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-canard" /> {v.seats} {t("vehicle.seats")}</span>
          <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5 text-canard" /> {v.transmission}</span>
          <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5 text-canard" /> {v.fuel}</span>
        </div>
        <div className="flex items-center gap-1 mt-3 text-xs text-emeraude-dark font-semibold">
          <ShieldCheck className="w-3.5 h-3.5" /> {t("vehicle.deposit")}: {v.deposit} {t("common.currency")}
        </div>
        <div className="flex items-end justify-between mt-4 pt-4 border-t border-border">
          <div>
            {v.dynamic_price_per_day && v.dynamic_price_per_day !== v.price_per_day ? (
              <>
                <span className="text-sm text-muted2 line-through mr-1">{v.price_per_day}</span>
                <span className="text-2xl font-heading font-extrabold text-canard-dark">{v.dynamic_price_per_day} {t("common.currency")}</span>
              </>
            ) : (
              <span className="text-2xl font-heading font-extrabold text-canard-dark">{v.price_per_day} {t("common.currency")}</span>
            )}
            <span className="text-xs text-muted2 font-semibold">{t("vehicle.perDay")}</span>
          </div>
          <Button data-testid={`vehicle-book-button-${v.id}`} onClick={() => nav(`/vehicle/${v.id}`)}
            className="rounded-full bg-emeraude hover:bg-emeraude-dark text-white font-bold px-5">
            {t("vehicle.details")}
          </Button>
        </div>
      </div>
    </div>
  );
}
